<template>
  <section
    :id="`fk-block-${id}`"
    class="fk-block !mb-6 lg:!mb-base bg-white overflow-hidden relative z-10"
    :class="sectionClasses"
  >
    <div
      class="!px-0 relative lg:absolute lg:right-0 inset-y-0 !max-w-none lg:!w-[calc(50vw-(2vw+2rem))] aspect-[3/1] md:aspect-[3/1] lg:aspect-auto -z-10 -mb-base-2"
    >
      <div class="bg-gradient-to-t from-white absolute inset-0 lg:hidden"></div>
      <NuxtPicture
        v-if="
          item?.attributes.headerImage && item?.attributes.headerImage?.data
        "
        :key="`vacancy-header-image-${item?.attributes.headerImage?.data?.id}`"
        :provider="item?.attributes.headerImage?.data?.attributes.provider"
        :src="item?.attributes.headerImage?.data?.attributes.hash"
        :width="item?.attributes.headerImage?.data?.attributes.width"
        :height="item?.attributes.headerImage?.data?.attributes.height"
        :sizes="'xs:100vw'"
        :loading="'eager'"
        :alt="item?.attributes.headerImage?.data?.attributes.alternativeText"
        class="lg:absolute inset-0 size-full select-none block"
        :img-attrs="{
          class: 'object-cover size-full',
        }"
      />

      <NuxtPicture
        v-else-if="
          item?.attributes.company.data &&
          item?.attributes.company.data.attributes.headerImage &&
          item?.attributes.company.data.attributes.headerImage.data
        "
        :key="`vacancy-company-header-image-${item?.attributes.company.data.attributes.headerImage?.data?.id}`"
        :provider="
          item?.attributes.company.data.attributes.headerImage?.data?.attributes
            .provider
        "
        :src="
          item?.attributes.company.data.attributes.headerImage?.data?.attributes
            .hash
        "
        :width="
          item?.attributes.company.data.attributes.headerImage?.data?.attributes
            .width
        "
        :height="
          item?.attributes.company.data.attributes.headerImage?.data?.attributes
            .height
        "
        :sizes="'xs:100vw'"
        :loading="'eager'"
        :alt="
          item?.attributes.company.data.attributes.headerImage?.data?.attributes
            .alternativeText
        "
        class="lg:absolute inset-0 size-full select-none block"
        :img-attrs="{
          class: 'object-cover size-full',
        }"
      />
      <span v-else class="lg:absolute inset-0 size-full select-none block">
        <img
          src="https://res.cloudinary.com/werkgeluk/image/upload/v1722947475/default_mhekan.jpg"
          alt="Default"
          class="object-cover size-full"
        />
      </span>
    </div>
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-base">
      <div class="pt-base lg:py-base-2">
        <!-- Vacancy Title, Heading 1 because of SEO importance -->
        <div class="fk-prose">
          <h1
            v-if="item?.attributes.title"
            class="!text-[clamp(2rem,5vw,2.5rem)]"
          >
            {{ item?.attributes.title }}
          </h1>
        </div>

        <!-- Labels -->
        <VacancyLabels
          :extra-classes="'mt-4'"
          :hours-from="item?.attributes.hoursFrom"
          :hours-to="item?.attributes.hoursTo"
          :educations="item?.attributes.educations.data"
          :monthly-salary-from="item?.attributes.monthlySalaryFrom"
          :monthly-salary-to="item?.attributes.monthlySalaryTo"
          :hourly-salary-from="item?.attributes.hourlySalaryFrom"
          :hourly-salary-to="item?.attributes.hourlySalaryTo"
          :salary-description="item?.attributes.salaryDescription"
        />

        <!--TODO: What to do with other statuses than open? Redirect if cancelled for example? -->
        <NuxtLink
          v-if="item?.attributes.status !== 'open'"
          :to="localePath(`/vacatures`)"
          class="bg-red-300 block p-3 my-3"
        >
          {{
            $t("general.vacancyStatusNotOpen", {
              region: region?.attributes.municipality,
            })
          }}
          <div class="mt-2">
            <span class="btn">{{ $t("general.viewAllVacancies") }}</span>
          </div>
        </NuxtLink>

        <!-- Company Logo -->
        <div class="my-4 flex gap-4 items-center">
          <NuxtLink
            v-if="item?.attributes.company.data"
            :to="`/bedrijven/${item?.attributes.company.data.attributes.slug}`"
            class="w-24 aspect-[4/3] flex-0"
          >
            <NuxtPicture
              v-if="
                item?.attributes.company.data &&
                item?.attributes.company.data.attributes.logo &&
                item?.attributes.company.data.attributes.logo.data
              "
              :provider="
                item?.attributes.company.data.attributes.logo.data.attributes
                  .provider
              "
              :src="
                item?.attributes.company.data.attributes.logo.data.attributes
                  .hash
              "
              :width="
                item?.attributes.company.data.attributes.logo.data.attributes
                  .width
              "
              :height="
                item?.attributes.company.data.attributes.logo.data.attributes
                  .height
              "
              :sizes="'xs:100vw sm:100vw md:50vw lg:33vw xl:33vw xxl:33vw'"
              :preload="true"
              :loading="'eager'"
              :alt="
                item?.attributes.company.data.attributes.logo.data.attributes
                  .alternativeText
              "
              :img-attrs="{
                class:
                  'object-contain h-full w-full group-hover:scale-110 transition-transform',
              }"
            />
          </NuxtLink>

          <div class="leading-none">
            <!-- Company Title -->
            <NuxtLink
              v-if="item?.attributes.company.data"
              :to="`/bedrijven/${item?.attributes.company.data.attributes.slug}`"
              class="font-bold text-lg leading-none"
            >
              {{ item?.attributes.company.data.attributes.title }}
            </NuxtLink>
            <!-- Vacancy Places -->
            <div v-if="item?.attributes.places.data.length">
              <NuxtLink
                v-for="(place, index) in item?.attributes.places.data"
                :key="`${item?.id}-vacancy-single-place-${place.id}`"
                :to="`/vacatures-in/${place.attributes.slug}`"
              >
                {{
                  place.attributes.title +
                  (index < item?.attributes.places.data.length - 1 ? ", " : "")
                }}
              </NuxtLink>
            </div>
          </div>
        </div>

        <VacancyShareAndApply
          class="[&>div:first-child]:hidden mt-8 hidden lg:block"
          :vacancy-title="item.attributes.title"
          :vacancy-status="item.attributes.status"
          :vacancy-external-apply-url="item.attributes.externalApplyUrl"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { BlockIntro } from "@flyingkiwi/nuxt-components";
const props = defineProps<BlockIntro>();
const { blockClasses } = useBlock();
const sectionClasses = computed(() => {
  return blockClasses(props);
});

const { itemKey } = useInjectionKeys();
const item = inject(itemKey);

const localePath = useLocalePath();

const { region } = useStaticData();
</script>
